import { useEffect, useMemo, useState } from 'react'
import { supabase } from '@/lib/supabase'
import { DailyTotals } from '@/types'
import { AnalyticsSelectedRange } from '@/hooks/useAnalyticsTimeFilter'
import { CrmTotalsViewModel, CrmPaymentChannel } from '@/components/analytics/CrmComparisonModal'

interface CrmNormalizedPayment {
  metricDate: string
  amount: number
  isSubscriptionSale: boolean
  paymentMethod: string
  lastSyncAt: string | null
  studioNumber: number | null
}

function firstNonEmpty<T>(...values: Array<T | null | undefined>): T | null {
  for (const value of values) {
    if (value !== null && value !== undefined && value !== '') return value
  }
  return null
}

function toNumber(value: unknown): number {
  if (typeof value === 'number') return Number.isFinite(value) ? value : 0
  if (typeof value === 'string') {
    const normalized = value.replace(/\s/g, '').replace(',', '.')
    const parsed = Number(normalized)
    return Number.isFinite(parsed) ? parsed : 0
  }
  return 0
}

function normalizePaymentMethod(rawValue: unknown): string {
  if (typeof rawValue !== 'string') return 'не указан'
  const value = rawValue.trim().replace(/\s+/g, ' ')
  return value || 'не указан'
}

function dateToIso(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
}

function normalizeDateOnly(value: unknown): string | null {
  if (!value) return null
  if (typeof value === 'string') {
    const trimmed = value.trim()
    if (!trimmed) return null
    if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) return trimmed
    const parsed = new Date(trimmed)
    if (!Number.isNaN(parsed.getTime())) {
      return dateToIso(parsed)
    }
    return trimmed.slice(0, 10)
  }
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return dateToIso(value)
  }
  return null
}

function getDatesBetween(start: string, end: string): string[] {
  const result: string[] = []
  const startDate = new Date(start)
  const endDate = new Date(end)
  for (let date = new Date(startDate); date <= endDate; date.setDate(date.getDate() + 1)) {
    result.push(dateToIso(date))
  }
  return result
}

interface UseCrmRevenueComparisonParams {
  crmStudioNumber: number | null
  crmStudioNumbers?: number[]
  selectedRange: AnalyticsSelectedRange | null
  dailyData: DailyTotals[]
}

export function useCrmRevenueComparison({
  crmStudioNumber,
  crmStudioNumbers,
  selectedRange,
  dailyData
}: UseCrmRevenueComparisonParams) {
  const [crmPayments, setCrmPayments] = useState<CrmNormalizedPayment[]>([])
  const [crmLoading, setCrmLoading] = useState(false)
  const [crmError, setCrmError] = useState<string | null>(null)
  const normalizedStudioNumbers = useMemo(
    () =>
      Array.from(
        new Set(
          [
            ...(Array.isArray(crmStudioNumbers) ? crmStudioNumbers : []),
            ...(crmStudioNumber ? [crmStudioNumber] : [])
          ].filter((value): value is number => Number.isFinite(value) && value > 0)
        )
      ),
    [crmStudioNumber, crmStudioNumbers]
  )

  useEffect(() => {
    if (normalizedStudioNumbers.length === 0 || !selectedRange) {
      setCrmPayments([])
      return
    }

    let active = true

    const loadCrmPayments = async () => {
      try {
        setCrmLoading(true)
        setCrmError(null)

        let query = (supabase as any)
          .from('report_payments')
          .select('date, studio_number, amount, payment_method, last_sync_at, subscription_sale')
          .gte('date', selectedRange.start)
          .lte('date', selectedRange.end)

        if (normalizedStudioNumbers.length === 1) {
          query = query.eq('studio_number', normalizedStudioNumbers[0])
        } else {
          query = query.in('studio_number', normalizedStudioNumbers)
        }

        const response = await query

        if (response.error) {
          throw response.error
        }

        const rows: any[] = Array.isArray(response.data) ? response.data : []
        const normalized = rows
          .map((row) => {
            const metricDateRaw = firstNonEmpty<string>(row.date)
            const metricDateValue = normalizeDateOnly(metricDateRaw)
            if (!metricDateValue) return null

            const amount = toNumber(row.amount)

            return {
              metricDate: metricDateValue,
              amount,
              isSubscriptionSale: typeof row.subscription_sale === 'string' && row.subscription_sale.trim() === 'Да',
              paymentMethod: normalizePaymentMethod(firstNonEmpty(row.payment_method)),
              lastSyncAt: firstNonEmpty(row.last_sync_at),
              studioNumber: (() => {
                const parsed = Number(firstNonEmpty(row.studio_number))
                return Number.isFinite(parsed) ? parsed : null
              })()
            } as CrmNormalizedPayment
          })
          .filter((row): row is CrmNormalizedPayment => row !== null)
          .filter((row) => row.metricDate >= selectedRange.start && row.metricDate <= selectedRange.end)

        if (active) {
          setCrmPayments(normalized)
        }
      } catch (error: any) {
        console.error('Failed to load CRM payments:', error?.message || error)
        if (active) {
          setCrmPayments([])
          setCrmError(error?.message || 'Не удалось загрузить CRM-данные')
        }
      } finally {
        if (active) {
          setCrmLoading(false)
        }
      }
    }

    loadCrmPayments()

    return () => {
      active = false
    }
  }, [normalizedStudioNumbers, selectedRange?.start, selectedRange?.end])

  const appRevenueByDate = useMemo(() => {
    const map = new Map<string, number>()
    dailyData.forEach((day) => {
      map.set(day.metric_date, day.gross_revenue || 0)
    })
    return map
  }, [dailyData])

  const crmRevenueByDate = useMemo(() => {
    const map = new Map<string, number>()
    crmPayments.forEach((row) => {
      map.set(row.metricDate, (map.get(row.metricDate) || 0) + row.amount)
    })
    return map
  }, [crmPayments])

  const crmSubscriptionRevenueByDate = useMemo(() => {
    const map = new Map<string, number>()
    crmPayments.forEach((row) => {
      if (!row.isSubscriptionSale) return
      map.set(row.metricDate, (map.get(row.metricDate) || 0) + row.amount)
    })
    return map
  }, [crmPayments])

  const crmLastSyncAt = useMemo(() => {
    const timestamps = crmPayments
      .map((row) => row.lastSyncAt)
      .filter((value): value is string => Boolean(value))
    if (timestamps.length === 0) return null
    return timestamps.reduce((latest, current) =>
      new Date(current).getTime() > new Date(latest).getTime() ? current : latest
    )
  }, [crmPayments])

  const crmTotals = useMemo<CrmTotalsViewModel>(() => {
    if (!selectedRange) {
      return {
        appRevenue: 0,
        crmRevenue: null,
        delta: null,
        deltaPercent: null,
        crmSubscriptionRevenue: null,
        dailyRows: []
      }
    }

    const rangeDates = getDatesBetween(selectedRange.start, selectedRange.end)
    const dailyRows = rangeDates.map((date) => {
      const hasCrmData = crmRevenueByDate.has(date)
      const appRevenue = appRevenueByDate.get(date) || 0
      const crmRevenue = hasCrmData ? (crmRevenueByDate.get(date) || 0) : 0
      const crmSubscriptionRevenue = hasCrmData ? (crmSubscriptionRevenueByDate.get(date) || 0) : 0
      return {
        date,
        appRevenue,
        crmRevenue,
        crmSubscriptionRevenue,
        hasCrmData,
        delta: appRevenue - crmRevenue
      }
    })

    const appRevenue = dailyRows.reduce((sum, row) => sum + row.appRevenue, 0)
    const crmRevenue = dailyRows.reduce((sum, row) => sum + row.crmRevenue, 0)
    const crmSubscriptionRevenue = dailyRows.reduce((sum, row) => sum + row.crmSubscriptionRevenue, 0)
    const delta = appRevenue - crmRevenue
    const deltaPercent = crmRevenue !== 0
      ? (delta / crmRevenue) * 100
      : null

    return {
      appRevenue,
      crmRevenue,
      delta,
      deltaPercent,
      crmSubscriptionRevenue,
      dailyRows
    }
  }, [selectedRange, appRevenueByDate, crmRevenueByDate, crmSubscriptionRevenueByDate])

  const crmPaymentChannels = useMemo<CrmPaymentChannel[]>(() => {
    if (!selectedRange) return []
    const channelMap = new Map<string, number>()

    crmPayments
      .filter((row) => row.metricDate >= selectedRange.start && row.metricDate <= selectedRange.end)
      .forEach((row) => {
        channelMap.set(row.paymentMethod, (channelMap.get(row.paymentMethod) || 0) + row.amount)
      })

    const total = Array.from(channelMap.values()).reduce((sum, amount) => sum + amount, 0)
    if (total === 0) return []

    return Array.from(channelMap.entries())
      .map(([name, amount]) => ({
        name,
        amount,
        share: (amount / total) * 100
      }))
      .sort((a, b) => b.amount - a.amount)
  }, [crmPayments, selectedRange])

  const crmCoverage = useMemo(() => {
    const totalStudios = normalizedStudioNumbers.length
    if (totalStudios === 0) {
      return {
        totalStudios: 0,
        studiosWithCrmData: 0,
        hasPartialCoverage: false
      }
    }

    const studiosWithCrmData = new Set(
      crmPayments
        .map((row) => row.studioNumber)
        .filter((value): value is number => value !== null)
    ).size

    return {
      totalStudios,
      studiosWithCrmData,
      hasPartialCoverage: studiosWithCrmData > 0 && studiosWithCrmData < totalStudios
    }
  }, [crmPayments, normalizedStudioNumbers])

  return {
    crmLoading,
    crmError,
    crmLastSyncAt,
    crmTotals,
    crmPaymentChannels,
    crmCoverage
  }
}
